import React from 'react';
import { Input, Textarea } from '@roger/ui-components';
import { useBespokeSectionContext } from './BespokeSectionContext';
import { useQuestionFieldDisabled } from './useQuestionFieldDisabled';
import { QidTag } from '../../../dev/QidTag';
import {
  joinClasses,
  BESPOKE_FIELD_LABEL_CLASS,
  BESPOKE_FIELD_STACK_GAP_CLASS,
  BESPOKE_INPUT_COMPACT_CLASS,
  BESPOKE_TEXTAREA_COMPACT_CLASS,
} from './fieldClassSlots';
import { resolveControlClassName } from './resolveFieldClassNames';

export interface OASISTextRenderProps {
  value: string;
  onChange: (v: string) => void;
  label: string;
}

export interface OASISTextProps {
  qid: string;
  /** Display-only replacement for the schema prompt; answer binding still uses qid. */
  labelOverride?: string;
  className?: string;
  style?: React.CSSProperties;
  hideLabel?: boolean;
  innerClassName?: string;
  labelClassName?: string;
  /** Render a textarea instead of a single-line input */
  multiline?: boolean;
  controlClassName?: string;
  /** @deprecated Prefer `controlClassName`. */
  inputClassName?: string;
  children?: (props: OASISTextRenderProps) => React.ReactNode;
}

export function OASISText({
  qid,
  labelOverride,
  className,
  style,
  hideLabel,
  innerClassName,
  labelClassName,
  multiline,
  controlClassName,
  inputClassName,
  children,
}: OASISTextProps) {
  const { answers, onAnswerChange, schemaMap } = useBespokeSectionContext();
  const question = schemaMap.get(qid);
  const inheritedDisabled = useQuestionFieldDisabled(question);

  const value = (answers[qid] as string) ?? '';
  const onChange = (v: string) => onAnswerChange(qid, v);
  const label =
    labelOverride ?? question?.question ?? question?.description ?? qid;
  const resolvedControlClassName = resolveControlClassName(
    controlClassName,
    inputClassName
  );

  return (
    <div
      data-qid={qid}
      className={joinClasses('min-w-0', className)}
      style={style}
    >
      <QidTag qid={qid} />
      {children ? (
        children({ value, onChange, label })
      ) : (
        <div className={innerClassName ?? BESPOKE_FIELD_STACK_GAP_CLASS}>
          {!hideLabel && (
            <label
              className={joinClasses(BESPOKE_FIELD_LABEL_CLASS, labelClassName)}
            >
              {label}
            </label>
          )}
          {multiline ? (
            <Textarea
              disabled={inheritedDisabled}
              value={value}
              onChange={e => onChange(e.target.value)}
              className={joinClasses(
                BESPOKE_TEXTAREA_COMPACT_CLASS,
                resolvedControlClassName
              )}
            />
          ) : (
            <Input
              disabled={inheritedDisabled}
              value={value}
              onChange={e => onChange(e.target.value)}
              className={joinClasses(
                BESPOKE_INPUT_COMPACT_CLASS,
                resolvedControlClassName
              )}
            />
          )}
        </div>
      )}
    </div>
  );
}
